import { usePagination } from '@uhg-abyss/web/hooks/usePagination';
import { DropdownMenu } from '@uhg-abyss/web/ui/DropdownMenu';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { I18nProvider } from '@uhg-abyss/web/ui/I18nProvider';
import { Pagination } from '@uhg-abyss/web/ui/Pagination';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../../../common/AdobeTagging/adobeModalTrackEvent';
import {
  ANALYTICS_CONSTANTS,
  canFetchCareTeam,
  filterCareTeamChipsByCoverageType,
  getIsSuperUser,
  isInvalidCurrentPcpID,
  isInvalidFuturePcpID,
  shouldHidePcpCard,
  shouldHidePcpCheck,
} from '../../../../common/CareTeamCard/CareTeamUtils';
import {
  CareTeamEmptyCard,
  ChoosePCPCard,
} from '../../../../common/CareTeamEmptyState';
import { CareTeamProviderCard } from '../../../../common/CareTeamProviderCard';
import {
  CareCategories,
  CareCategoryType,
  Constants,
  CoverageTypesCodes,
  ReverseCoverageTypesCodes,
  sortOptions,
} from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { mobileOnly } from '../../../../common/ConstantsStyles';
import { LoadingSpinnerComp } from '../../../../common/Loader';
import { getFeatureFlags } from '../../../../common/Utils';
import { isUmrMember } from '../../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../../context/PortalContext';
import {
  useGetCareTeam,
  useGetCareTeamPaginated,
} from '../../../../hooks/useCareTeam';
import { useCurrentMemberOrDependentInfo } from '../../../../hooks/useCurrentMember/useCurrentMember';
import { useCurrentMemberIsPreEffective } from '../../../../hooks/useCurrentMemberIsPreEffective';
import { useGeoLocationStorage } from '../../../../hooks/useGeoLocationStorage';
import { useHsidUuid } from '../../../../hooks/useHsidUuid';
import { useLagoon } from '../../../../hooks/useLagoon';
import { usePrimaryCare } from '../../../../hooks/usePrimaryCare';
import { useSmartChoice } from '../../../../hooks/useSmartChoice';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { ProviderType } from '../../../../models/ProviderDetails';
import { useCareTeamStore } from '../../../../store/useCareTeamStore';
import { CareTeamState } from '../../../../store/useCareTeamStore/careTeamStore';
import { useNonGatedPcpStore } from '../../../../store/useNonGatedPcpStore';
import { NonGatedPcpData } from '../../../../store/useNonGatedPcpStore/nonGatedPcpStore';
import { isPcpSuppressionEligible } from '../../../../utils/pcpSuppression.utils';
import { calculateTotalProviderCount } from '../../../../utils/primaryCare.utils';
import {
  getLagoonConfigValue,
  getRecommendationPayload,
} from '../../../../utils/providerSearch.utils';
import {
  extractCoverageWithNetwork,
  getCoverageEffDateRange,
  getIsOxfordAndUSP,
  getLoggedInMember,
  isGroupMNRMember,
  parseLOB,
} from '../../../../utils/user.utils';
import { getLanguage } from '../../context/Internationalization/helpers';
import {
  CardGrid,
  CareTeamChipsContainer,
  CareTeamDetailView,
  CareTeamViewWrapper,
  ChipsDropdownStyle,
  ChoosePCPWrapper,
  PaginationWrapper,
} from './CareTeamView.styled';
import { Chips } from './Chips';

const pageSize = 8;

const getHasDisplayableCurrentPcp = (
  pcpInfo,
  hasInvalidPcpID: boolean,
  isPcpSuppressed: boolean,
  nonGatedPcpId?: string | null
) => {
  const hasValidCurrentPcp = Boolean(pcpInfo?.pcpID) && !hasInvalidPcpID;
  const hasSuppressedNonGatedPcp = isPcpSuppressed && !!nonGatedPcpId;
  return hasValidCurrentPcp || hasSuppressedNonGatedPcp;
};

const getNonGatedPcpData = (
  isPcpSuppressed: boolean,
  nonGatedPrimaryCareData: unknown
) => {
  if (!isPcpSuppressed) {
    return { nonGatedPcpProviderType: null, nonGatedPcpId: null };
  }
  const data = nonGatedPrimaryCareData as NonGatedPcpData | null;
  return {
    nonGatedPcpProviderType: data?.providerType,
    nonGatedPcpId: data?.pcpId,
  };
};

export const CareTeamDetailPage = ({
  ENABLE_NEW_PCP_CARE_TEAM_FLOW,
  filteredPcpConfig,
  setPcpProvider,
  setFuturePcpProvider,
  setPCPChipValue,
  setParentPageNumber,
}) => {
  const { t } = useTranslation();
  const locale = getLanguage().code;
  const smartChoiceEligibility = useSmartChoice();
  const isSmartChoiceEnabled = smartChoiceEligibility.isAvailable;
  const preferenceData = useStore(StoreKeys.RECOMMENDATION_PREFERENCES);
  const recommendationPreferences = getRecommendationPayload();
  const featureFlags: [{ key: string; active: boolean }] = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_FLAGS
  )();

  const { nonGatedPrimaryCareData, hasFetchedNonGatedPrimaryCareData } =
    useNonGatedPcpStore(
      useShallow((state) => ({
        nonGatedPrimaryCareData: state.nonGatedPrimaryCareData,
        hasFetchedNonGatedPrimaryCareData:
          state.hasFetchedNonGatedPrimaryCareData,
      }))
    );

  const { portalData } = useContext(PortalContext);
  const allConfigs = useLagoon('config');
  const defaultSearchRadius = getLagoonConfigValue(
    allConfigs,
    'DEFAULT_SEARCH_RADIUS'
  );
  const isSuperUser = getIsSuperUser(allConfigs);
  const [enableStaffModel, enableStaffModelEni] = getFeatureFlags(
    featureFlags,
    [
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_ALL_STAFF_MODEL_MNR,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_ALL_STAFF_MODEL_ENI,
    ]
  );
  const primaryCareData = usePrimaryCare();
  const [loading, setLoading] = useState(false);
  const [chipCountData, getCareTeam] = useGetCareTeam({
    onCompleted: (data: any) => {
      if (
        data?.data?.getCareTeam?.chipWithCount?.length === 0 &&
        !pcpInfo?.providerID
      ) {
        setLoading(false);
      } else {
        setLoading(true);
      }
    },
  });
  const hsidUuid = useHsidUuid();
  const { longitude, latitude } = useGeoLocationStorage();
  const loggedInMemberInfo = getLoggedInMember();
  const isPreEffective = useCurrentMemberIsPreEffective();
  const { setCareTeamRecords, careTeamRecords } = useCareTeamStore(
    useShallow((state: CareTeamState) => state)
  );
  const sortByValueFromStore = sortOptions?.find(
    (option) => option.id === careTeamRecords?.sortByValue
  );
  const [sortByField, setSortByField] = useState(
    sortByValueFromStore?.sortByField || sortOptions[0]?.sortByField
  );
  const [sortDirection, setSortDirection] = useState(
    sortByValueFromStore?.sortDirection || sortOptions[0]?.sortDirection
  );
  const [selectedSortOption, setSelectedSortOption] = useState(
    sortByValueFromStore?.sortOptionLabel || sortOptions[0]?.sortOptionLabel
  );
  const [careTeamData, getCareTeamPaginated] = useGetCareTeamPaginated({
    onCompleted: (data: any) => {
      if (data?.data?.getCareTeamPaginated?.careTeamProviders) {
        setLoading(false);
        setCareTeamProviders(
          data?.data?.getCareTeamPaginated?.careTeamProviders || []
        );
      }
    },
  });
  const [careTeamProviders, setCareTeamProviders] = useState(
    careTeamData?.data.careTeamProviders || []
  );

  useEffect(() => {
    if (careTeamData?.data?.careTeamProviders) {
      let pcp = careTeamProviders.find((provider) => provider.isPcp);
      if (!pcp) {
        pcp = { status: 'NA' };
      }
      setPcpProvider(pcp);

      const futurePcp = careTeamProviders.find(
        (provider) => provider.isFuturePcp
      );
      if (setFuturePcpProvider) {
        setFuturePcpProvider(futurePcp);
      }
    }
  }, [careTeamData?.data?.careTeamProviders]);

  const isStaffModelPCPInNetwork = careTeamProviders?.some(
    (provider) =>
      provider.isPcp &&
      provider.providerLocations?.some((loc) => loc.network?.status === 'INN')
  );

  const isStaffModelFuturePCPInNetwork = careTeamProviders?.some(
    (provider) =>
      provider.isFuturePcp &&
      provider.providerLocations?.some((loc) => loc.network?.status === 'INN')
  );

  const mobileScreen = useMediaQuery(mobileOnly);
  const { primaryCare, hasPCPFetchError } = primaryCareData?.data;
  const dependentSeqNbr = loggedInMemberInfo?.demographics?.dependentSeqNbr;

  const pcpInfo = primaryCare?.find(
    ({ depSeqNbr }) => dependentSeqNbr === depSeqNbr
  )?.pcpInfo;
  const hasInvalidPcpID = isInvalidCurrentPcpID(pcpInfo);
  const hasInvalidFuturePcpID = isInvalidFuturePcpID(pcpInfo);

  const currentMember = useCurrentMemberOrDependentInfo(dependentSeqNbr);

  const enableNonGatedPcpSuppression =
    featureFlags.find((flag) => flag.key === 'ENABLE_NON_GATED_PCP_SUPPRESSION')
      ?.active ?? false;
  const isPcpSuppressed = isPcpSuppressionEligible(
    currentMember,
    enableNonGatedPcpSuppression
  );

  const { nonGatedPcpProviderType, nonGatedPcpId } = getNonGatedPcpData(
    isPcpSuppressed,
    nonGatedPrimaryCareData
  );

  const hidePcpCheck = shouldHidePcpCheck(
    loggedInMemberInfo?.viewPcp,
    isGroupMNRMember(loggedInMemberInfo)
  );

  const isCurrentPcpCardVisible =
    filteredPcpConfig?.showCurrentPcpCard && !hasInvalidPcpID;
  const isFuturePcpCardVisible =
    filteredPcpConfig?.showFuturePcpCard && !hasInvalidFuturePcpID;

  const hidePCPCard = shouldHidePcpCard(
    isPcpSuppressed,
    nonGatedPcpId,
    ENABLE_NEW_PCP_CARE_TEAM_FLOW,
    hidePcpCheck,
    hasPCPFetchError,
    filteredPcpConfig?.showCurrentPcpCard,
    filteredPcpConfig?.showFuturePcpCard,
    isCurrentPcpCardVisible,
    isFuturePcpCardVisible,
    hasFetchedNonGatedPrimaryCareData
  );

  const categories = filterCareTeamChipsByCoverageType(
    Object.keys(CareCategoryType),
    currentMember
  );

  const [selectedCategory, setSelectedCategory] = useState<string>(
    careTeamRecords?.chipValue || categories[0]
  );
  const [pageNumber, setPageNumber] = useState<number>(
    careTeamRecords?.pageNumber || 1
  );

  useEffect(() => {
    setPCPChipValue && setPCPChipValue(selectedCategory);
  }, [selectedCategory, setPCPChipValue]);

  useEffect(() => {
    setParentPageNumber && setParentPageNumber(pageNumber);
  }, [pageNumber, setParentPageNumber]);

  const chipsCount = chipCountData?.data?.getCareTeam?.chipWithCount || {};

  const coverageWithNetwork = extractCoverageWithNetwork(
    currentMember,
    featureFlags
  );

  const selectedCoverageType = useMemo(
    () =>
      selectedCategory
        ? ReverseCoverageTypesCodes[
            find(Constants.MOCK_CARE_CATEGORIES, {
              value: CareCategories[selectedCategory],
            })?.coverageType || CoverageTypesCodes.MEDICAL
          ]
        : '',
    [selectedCategory]
  );

  const pcpData = {
    ...pcpInfo,
    ...careTeamProviders?.find((provider) => provider?.isPcp),
  };

  const futurePcpData = {
    ...pcpInfo?.futurePCP,
    ...careTeamProviders?.find((provider) => provider?.isFuturePcp),
  };

  const hasFuturePCP = pcpInfo && pcpInfo.futurePCP != null;
  const hasDisplayableCurrentPcp = getHasDisplayableCurrentPcp(
    pcpInfo,
    hasInvalidPcpID,
    isPcpSuppressed,
    nonGatedPcpId
  );
  const hasDisplayableFuturePcp = hasFuturePCP && !hasInvalidFuturePcpID;

  const pcpCardCount = useMemo(() => {
    if (hidePCPCard || isPcpSuppressed) return 0;

    if (ENABLE_NEW_PCP_CARE_TEAM_FLOW) {
      const showCurrentPcpCard =
        (filteredPcpConfig?.showCurrentPcpCard || false) && !hasInvalidPcpID;
      const showFuturePcpCard =
        (filteredPcpConfig?.showFuturePcpCard || false) &&
        !hasInvalidFuturePcpID;
      if (showCurrentPcpCard && showFuturePcpCard) return 2;
      if (showCurrentPcpCard || showFuturePcpCard) return 1;
      return 1;
    }

    return 1;
  }, [
    hidePCPCard,
    isPcpSuppressed,
    ENABLE_NEW_PCP_CARE_TEAM_FLOW,
    filteredPcpConfig,
    hasInvalidPcpID,
    hasInvalidFuturePcpID,
  ]);

  const getCount = useCallback(
    (category: string | undefined) => {
      if (!category || !chipCountData?.data?.getCareTeam?.chipWithCount) {
        return 0;
      }

      const baseCount = chipsCount[CareCategories[category]] || 0;
      const isMedicalSpecialists =
        CareCategories[category] === CareCategories.MEDICAL_SPECIALISTS;

      return isMedicalSpecialists
        ? calculateTotalProviderCount(
            ENABLE_NEW_PCP_CARE_TEAM_FLOW,
            baseCount,
            hasDisplayableCurrentPcp,
            hasDisplayableFuturePcp
          )
        : baseCount;
    },
    [
      chipsCount,
      ENABLE_NEW_PCP_CARE_TEAM_FLOW,
      hasDisplayableCurrentPcp,
      hasDisplayableFuturePcp,
    ]
  );
  const hasAnyProviders = useMemo(
    () => categories.some((category) => getCount(category) > 0),
    [chipsCount]
  );
  const { careTeamProviderCount } = useCareTeamStore((state) => ({
    careTeamProviderCount: state.careTeamProviderCount,
  }));

  const { state, goToPage, nextPage, previousPage, ...paginationProps } =
    usePagination({
      start: pageNumber,
      pages: Math.max(
        Math.ceil(
          (selectedCategory === categories[0]
            ? chipsCount[CareCategories[selectedCategory]] + pcpCardCount
            : chipsCount[CareCategories[selectedCategory]]) / pageSize
        ),
        1
      ),
    });

  useEffect(() => {
    if (careTeamRecords?.pageNumber !== state?.currentPage) {
      setPageNumber(state?.currentPage);
      setCareTeamRecords({
        pageNumber: state?.currentPage,
      });
    }
  }, [state?.currentPage]);

  const isStaffModelMember =
    enableStaffModel &&
    (!!pcpInfo?.isStaffModel || !!pcpInfo?.futurePCP?.isStaffModel);

  const staffModelPcpProvider = isStaffModelMember
    ? careTeamProviders?.find(
        (provider) =>
          provider.isPcp &&
          provider.providerLocations?.some(
            (loc) => loc.network?.status === 'INN'
          )
      )
    : null;

  const staffModelFuturePcpProvider = isStaffModelMember
    ? careTeamProviders?.find(
        (provider) =>
          provider.isFuturePcp &&
          provider.providerLocations?.some(
            (loc) => loc.network?.status === 'INN'
          )
      )
    : null;

  const staffModelPcpData = isStaffModelMember
    ? { ...pcpInfo, isPcp: true, ...(staffModelPcpProvider || {}) }
    : pcpData;

  const staffModelFuturePcpData = isStaffModelMember
    ? {
        ...pcpInfo?.futurePCP,
        isFuturePcp: true,
        ...(staffModelFuturePcpProvider || {}),
      }
    : futurePcpData;

  const providerData = isPcpSuppressed
    ? careTeamProviders
    : careTeamProviders?.filter(
        (provider) => !provider?.isPcp && !provider?.isFuturePcp
      );
  const { isOXMember, isUSPMember } = getIsOxfordAndUSP(
    currentMember,
    selectedCoverageType,
    featureFlags
  );

  const setCategory = (category: string) => {
    setSelectedCategory(category);
    goToPage(0);
    setPageNumber(1);
    setSortByField(sortOptions[0]?.sortByField);
    setSortDirection(sortOptions[0]?.sortDirection);
    setSelectedSortOption(sortOptions[0]?.sortOptionLabel);
    careTeamRecords?.chipValue !== category &&
      setCareTeamRecords({
        chipValue: category,
        pageNumber: 1,
        sortByValue: sortOptions[0]?.id,
      });
  };

  useEffect(() => {
    if (getCount(selectedCategory) === 0) {
      const nextCategory = categories.find(
        (category) => getCount(category) > 0
      );
      if (nextCategory) {
        setCategory(nextCategory);
      } else {
        if (careTeamProviders?.length !== 0) {
          setCareTeamProviders([]);
        }
      }
    }
  }, [chipsCount, selectedCategory]);

  const handleSortByChange = (sortBy: string) => {
    const selectedOption = sortOptions?.find((option) => option.id === sortBy);
    if (selectedOption) {
      adobeLinkTrackEvent({
        name: selectedOption?.dataAnalyticName,
        location: ANALYTICS_CONSTANTS.SORT_BY,
        type: ANALYTICS_CONSTANTS.INTERNAL_TYPE,
      });

      setSortByField(selectedOption?.sortByField);
      setSortDirection(selectedOption?.sortDirection);
      setSelectedSortOption(selectedOption?.sortOptionLabel);
      setCareTeamRecords({
        sortByValue: selectedOption?.id,
      });
      goToPage(0);
    }
  };

  useEffect(() => {
    const sortBy = careTeamRecords?.sortByValue || sortOptions[0].id;
    const selectedOption = sortOptions?.find((option) => option.id === sortBy);
    if (selectedOption) {
      setSortByField(selectedOption?.sortByField);
      setSortDirection(selectedOption?.sortDirection);
      setSelectedSortOption(selectedOption?.sortOptionLabel);
    }
  }, [careTeamRecords?.sortByValue]);

  const sortByMenuItems = sortOptions.map((option) => ({
    title: t(option.sortOptionLabel),
    value: option.id,
    onClick: () => handleSortByChange(option.id),
  }));

  const getEniStaffModelParams = () => {
    const params: Record<string, any> = {};
    if (!currentMember?.isGatedUser) return null;
    if (
      enableStaffModelEni &&
      currentMember?.isUspNiceMember &&
      pcpInfo?.isStaffModel
    ) {
      params.pcpNetworkAffiliationId = pcpInfo?.networkTypeCode;
    }
    if (
      enableStaffModelEni &&
      currentMember?.isUspNiceMember &&
      pcpInfo?.futurePCP?.isStaffModel
    ) {
      params.futurePcpNetworkAffiliationId =
        pcpInfo?.futurePCP?.networkTypeCode;
    }
    return params;
  };

  const fetchCareTeam = useCallback(() => {
    const canFetch = canFetchCareTeam(
      primaryCare,
      hidePCPCard,
      isPcpSuppressed,
      hasFetchedNonGatedPrimaryCareData,
      isSuperUser,
      isSmartChoiceEnabled,
      preferenceData
    );

    const hasProviders = getCount(selectedCategory) > 0;

    if (canFetch && hasProviders) {
      // Fetch care team data based on selected category and current page
      setLoading(true);
      const isFirstPageMedical =
        selectedCategory === categories[0] && pageNumber === 1;
      const offset = isFirstPageMedical
        ? 0
        : (pageNumber - 1) * pageSize -
          (selectedCategory === categories[0] ? pcpCardCount : 0);

      const memberEffectiveDate = getCoverageEffDateRange(
        currentMember,
        'M'
      ).startDate;

      const resolvedPcpIdentifier = isPcpSuppressed
        ? nonGatedPcpId
        : pcpInfo?.pcpID;
      const resolvedFuturePcpIdentifier = pcpInfo?.futurePCP
        ? pcpInfo.futurePCP.pcpID
        : null;
      const resolvedPcpProviderType = isPcpSuppressed
        ? ProviderType[nonGatedPcpProviderType as string]
        : ProviderType[pcpInfo?.providerType];

      const identifierSplitCount =
        Number(Boolean(resolvedPcpIdentifier)) +
        Number(Boolean(resolvedFuturePcpIdentifier));
      const remainingPageSize = Math.max(0, pageSize - identifierSplitCount);

      getCareTeamPaginated({
        variables: JSON.parse(
          decodeURI(
            JSON.stringify({
              groupNumber: currentMember?.policyNumber || '',
              chipValue: CareCategories[selectedCategory],
              pageOffset: offset,
              pageSize: isFirstPageMedical ? remainingPageSize : pageSize,
              sortByField,
              sortDirection,
              latitude,
              longitude,
              coverageWithNetwork,
              memberEffectiveDate,
              isSuperUser,
              pcpProviderType: isFirstPageMedical
                ? resolvedPcpProviderType
                : '',
              futurePcpProviderType: isFirstPageMedical
                ? ProviderType[pcpInfo?.futurePCP?.providerType]
                : '',
              isOXMember,
              isUSPMember,
              lob: parseLOB(currentMember?.lineOfBusiness),
              population: currentMember?.population,
              locale,
              ...(isFirstPageMedical && {
                pcpIdentifier: resolvedPcpIdentifier,
              }),
              ...(isFirstPageMedical &&
                resolvedFuturePcpIdentifier && {
                  futurePcpIdentifier: resolvedFuturePcpIdentifier,
                }),
              ...getEniStaffModelParams(),
              ...(hsidUuid && { userId: hsidUuid }),
              ...(isSmartChoiceEnabled && {
                recommendationPreferences: {
                  ...recommendationPreferences,
                  recommendationRadius: Number(defaultSearchRadius),
                },
                isProvRecommendationToggleOn: true,
              }),
            })
          )
        ),
      });
    }
  }, [
    pageNumber,
    selectedCategory,
    primaryCare,
    sortByField,
    sortDirection,
    hidePCPCard,
    isSuperUser,
    getCount(selectedCategory),
    pcpCardCount,
    preferenceData,
    isSmartChoiceEnabled,
    isPcpSuppressed,
    hasFetchedNonGatedPrimaryCareData,
    nonGatedPcpId,
    nonGatedPcpProviderType,
  ]);

  const fetchChipCount = useCallback(() => {
    getCareTeam({
      variables: {
        groupNumber: currentMember?.policyNumber,
        toFetchChipCount: true,
        isSuperUser,
        ...(hsidUuid && { userId: hsidUuid }),
      },
    });
  }, [getCareTeam, getCareTeamPaginated, fetchCareTeam]);

  useEffect(() => {
    const canFetch = canFetchCareTeam(
      primaryCare,
      hidePCPCard,
      isPcpSuppressed,
      hasFetchedNonGatedPrimaryCareData,
      isSuperUser
    );
    if (canFetch) fetchChipCount();
  }, [
    primaryCareData?.isLoading,
    isSuperUser,
    primaryCare,
    hidePCPCard,
    isPcpSuppressed,
    hasFetchedNonGatedPrimaryCareData,
  ]);

  useEffect(() => {
    fetchCareTeam();
  }, [fetchCareTeam]);

  const removeCallback = useCallback(() => {
    const chipCount = getCount(selectedCategory) - 1;
    fetchChipCount();

    if (!(chipCount === 1 || careTeamProviders?.length > 1)) {
      goToPage(pageNumber - 2);
    }
  }, [
    fetchChipCount,
    getCount,
    selectedCategory,
    categories,
    pageNumber,
    providerData,
    fetchCareTeam,
    goToPage,
  ]);

  const menuItems = [
    {
      checkboxes: categories.map((category) => ({
        label: `${t(CareCategories[category])} (${getCount(category)})`,
        value: category,
        checked: selectedCategory === CareCategoryType[category],
        onChange: () => setCategory(category),
        disabled: getCount(category) === 0,
      })),
    },
  ];

  const isUmr = isUmrMember(portalData?.portalName);

  const renderFilterAndSortControls = () => {
    if (!hasAnyProviders || !selectedCategory) {
      return null;
    }

    if (mobileScreen) {
      return (
        <div
          className="sticky-header container"
          style={isUmr ? { top: 0 } : {}}
        >
          <CareTeamChipsContainer>
            <Text color="black" data-testid="filter-by-category" fontSize="md">
              {`${t('FILTER_LABELS.FILTER_BY_CATEGORY')}:`}
            </Text>
            <DropdownMenu
              css={ChipsDropdownStyle}
              label={`${t(CareCategoryType[selectedCategory])} (${getCount(
                selectedCategory
              )})`}
              menuItems={menuItems}
              outline
            />
          </CareTeamChipsContainer>
          <CareTeamChipsContainer>
            <Text color="black" data-testid="sort-by" fontSize="md">
              {`${t('FILTER_LABELS.SORT_BY')}:`}
            </Text>
            <DropdownMenu
              css={ChipsDropdownStyle}
              data-testid="sort-by-dropdown"
              label={t(selectedSortOption)}
              menuItems={sortByMenuItems}
              onOpenChange={(open) => {
                open && adobeModalTrackEvent(ANALYTICS_CONSTANTS.SORT_RESULTS);
              }}
              outline
            />
          </CareTeamChipsContainer>
        </div>
      );
    }

    return (
      <Flex
        className="sticky-header container"
        justify="space-between"
        style={isUmr ? { top: 0 } : {}}
      >
        <div>
          <Text color="black" data-testid="filter-by-category" fontSize="md">
            {`${t('FILTER_LABELS.FILTER_BY_CATEGORY')}:`}
          </Text>
          <CareTeamChipsContainer>
            {categories.map((category) => (
              <Chips
                commonWrapperStyle={{ marginRight: '20px' }}
                count={getCount(category)}
                disabled={getCount(category) === 0}
                isSelected={selectedCategory === category}
                key={category}
                onPress={() => setCategory(category)}
                text={t(CareCategoryType[category])}
              />
            ))}
          </CareTeamChipsContainer>
        </div>
        <div>
          <Text color="black" data-testid="sort-by" fontSize="md">
            {`${t('FILTER_LABELS.SORT_BY')}:`}
          </Text>
          <DropdownMenu
            css={{
              'abyss-dropdown-menu-trigger-root': {
                cursor: 'pointer',
                marginTop: '10px',
              },
            }}
            data-testid="sort-by-dropdown"
            label={t(selectedSortOption)}
            menuItems={sortByMenuItems}
            onOpenChange={(open) => {
              open && adobeModalTrackEvent(ANALYTICS_CONSTANTS.SORT_RESULTS);
            }}
            outline
          />
        </div>
      </Flex>
    );
  };

  const isFirstPageHasMedicalCategory =
    selectedCategory === categories[0] &&
    pageNumber == 1 &&
    !hidePCPCard &&
    !isPcpSuppressed;

  const renderPcpCards = () => (
    <>
      {filteredPcpConfig?.showCurrentPcpCard && !hasInvalidPcpID && (
        <ChoosePCPCard
          dependentSeqNbr={dependentSeqNbr}
          isFuturePcp={false}
          isPreEffective={isPreEffective}
          isStaffModelMember={isStaffModelMember}
          isStaffModelPCPInNetwork={isStaffModelPCPInNetwork}
          pcpConfig={filteredPcpConfig}
          pcpData={staffModelPcpData}
        />
      )}
      {filteredPcpConfig?.showFuturePcpCard && !hasInvalidFuturePcpID && (
        <ChoosePCPCard
          dependentSeqNbr={dependentSeqNbr}
          isFuturePcp={true}
          isPreEffective={isPreEffective}
          isStaffModelMember={isStaffModelMember}
          isStaffModelPCPInNetwork={isStaffModelFuturePCPInNetwork}
          pcpConfig={filteredPcpConfig}
          pcpData={staffModelFuturePcpData}
        />
      )}
      {!ENABLE_NEW_PCP_CARE_TEAM_FLOW && (
        <ChoosePCPCard
          dependentSeqNbr={dependentSeqNbr}
          isFuturePcp={false}
          isPreEffective={isPreEffective}
          isStaffModelMember={isStaffModelMember}
          isStaffModelPCPInNetwork={isStaffModelPCPInNetwork}
          pcpConfig={null}
          pcpData={staffModelPcpData}
        />
      )}
    </>
  );

  return (
    <React.Fragment>
      {chipCountData?.isLoading || primaryCareData?.isLoading ? (
        <LoadingSpinnerComp
          isLoading={chipCountData?.isLoading || primaryCareData?.isLoading}
        />
      ) : (
        <CareTeamViewWrapper data-testid="care-team-view-wrapper">
          {renderFilterAndSortControls()}
          {loading ? (
            <LoadingSpinnerComp isLoading={loading} />
          ) : (
            <div className="container">
              <CareTeamDetailView>
                {providerData.length === 0 ? (
                  <>
                    {!careTeamData?.error &&
                      pageNumber == 1 &&
                      (hidePCPCard ? null : (
                        <ChoosePCPWrapper>{renderPcpCards()}</ChoosePCPWrapper>
                      ))}
                    {careTeamProviderCount === 0 && (
                      <CareTeamEmptyCard
                        filteredPcpConfig={filteredPcpConfig}
                      />
                    )}
                  </>
                ) : (
                  <CardGrid>
                    {isFirstPageHasMedicalCategory && renderPcpCards()}
                    {providerData.length > 0 &&
                      providerData.map((provider, index) => {
                        const {
                          imageLocation,
                          isTieredProvider,
                          llmCodes,
                          networkStatus,
                          organizationType,
                          primaryDegrees,
                          providerId,
                          providerName,
                          providerType,
                          specialities,
                          isPcp,
                          chipValue,
                          originalChipValue,
                        } = provider;

                        return (
                          <CareTeamProviderCard
                            chipValue={chipValue}
                            coverageType={selectedCoverageType}
                            data-testid={`care-team-provider-card-${index}`}
                            dependentSeqNbr={dependentSeqNbr}
                            imgSrc={imageLocation}
                            isPcP={isPcp}
                            isPreEffective={isPreEffective}
                            isStaffModelMember={isStaffModelMember}
                            isTieredPlan={true}
                            isTieredProvider={isTieredProvider}
                            key={index}
                            llmCodes={llmCodes}
                            networkStatus={networkStatus}
                            organizationType={organizationType}
                            originalChipValue={originalChipValue}
                            professionalDesignation={
                              (primaryDegrees && primaryDegrees[0]) ?? ''
                            }
                            providerId={providerId}
                            providerName={providerName}
                            providerType={providerType}
                            removeCallback={removeCallback}
                            snackCardProvider={provider}
                            speciality={specialities
                              ?.map((speciality) => speciality)
                              .join(', ')}
                          />
                        );
                      })}
                  </CardGrid>
                )}
              </CareTeamDetailView>
              {getCount(selectedCategory) > pageSize && (
                <PaginationWrapper>
                  <I18nProvider
                    translations={{
                      Pagination: {
                        previous: t('PAGINATION.PREVIOUS'),
                        next: t('PAGINATION.NEXT'),
                      },
                    }}
                  >
                    <Pagination
                      {...paginationProps}
                      customNextLabel={t('PAGINATION.NEXT')}
                      customPreviousLabel={t('PAGINATION.PREVIOUS')}
                      data-testid="pagination"
                      goToPage={goToPage}
                      nextPage={nextPage}
                      previousPage={previousPage}
                      variant={mobileScreen ? 'minimal' : 'extended'}
                    />
                  </I18nProvider>
                </PaginationWrapper>
              )}
            </div>
          )}
        </CareTeamViewWrapper>
      )}
    </React.Fragment>
  );
};
